import { routeFactory } from "@/RouteFactory";
import { Link } from "@/components/Link";
import { formatDateStringWithLocalTimeZone } from "@/lib/dates/formatters";
import { Column, Heading, Row, Txt } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  FlatButton,
  ValueTable,
  ValueTableItem,
} from "@stenajs-webui/elements";
import styled from "@emotion/styled";
import * as React from "react";
import { useNavigate } from "react-router";
import { CustomerBox } from "../types";

const InfoTable = styled.div`
  td {
    vertical-align: top;
  }

  td > div {
    height: auto;
    min-height: var(--swui-value-table-height);
  }

  td:first-child * {
    --current-font-weight: 300 !important;
  }
`;

const Byline = styled(Txt)`
  --current-font-weight: 200 !important;
  padding-bottom: 6px;
`;

const formatTimestamp = (isoString: string | undefined | null) =>
  isoString
    ? formatDateStringWithLocalTimeZone({
        dateString: isoString,
        options: { dateStyle: "short", timeStyle: "short" },
      }) ?? ""
    : "";

const CustomerLink: React.FC<{
  customerId: string;
  label: React.ReactNode;
}> = ({ customerId, label }) => (
  <Link
    to={routeFactory.productAndPrice.customer.customer({
      customerId,
    })}
  >
    <Txt size="small" color="unset">
      {label}
    </Txt>
  </Link>
);

const ParentChildLink: React.FC<{ customer: CustomerBox }> = ({ customer }) => {
  if (customer.isParent) {
    const { length } = customer.childCustomers;
    return (
      <Link
        to={routeFactory.productAndPrice.customer.childCustomers({
          customerId: String(customer.custNo),
        })}
      >
        <Txt size="small" color="unset">
          {`Parent (${length} ${length === 1 ? "child" : "children"})`}
        </Txt>
      </Link>
    );
  }

  return (
    <CustomerLink
      customerId={customer.parent?.id ?? ""}
      label={`Child of #${customer.parent?.id}`}
    />
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => <ValueTableItem label={label} value={value} alignValue={"flex-end"} />;

const TimestampWithName: React.FC<{
  isoString: string | undefined | null;
  name: string | undefined | null;
}> = ({ isoString, name }) => (
  <Column alignItems={"flex-end"}>
    <Row height={"var(--swui-value-table-height)"} alignItems={"center"}>
      <Txt>{formatTimestamp(isoString)}</Txt>
    </Row>
    {name && <Byline size={"small"}>{name}</Byline>}
  </Column>
);

interface Props {
  customer: CustomerBox;
}

export const CustomerInfoCard: React.FC<Props> = ({ customer }) => {
  const showType = !customer.isParent || customer.childCustomers.length > 0;
  const navigate = useNavigate();

  return (
    <Card>
      <CardBody>
        <Row justifyContent={"space-between"}>
          <Heading>Booking account</Heading>
          <FlatButton
            label={"Price parent"}
            onClick={() =>
              navigate(
                routeFactory.productAndPrice.customer.customerPriceParent({
                  customerId: String(customer.custNo),
                })
              )
            }
          />
        </Row>
        <InfoTable>
          <ValueTable>
            <InfoRow label={"Index"} value={customer.custIndex} />
            <InfoRow
              label={"No."}
              value={
                <CustomerLink
                  customerId={String(customer.custNo)}
                  label={customer.custNo}
                />
              }
            />
            <InfoRow
              label={"Stat. No"}
              value={
                <CustomerLink
                  customerId={String(customer.statisticalGrouping?.id ?? "")}
                  label={customer.statisticalGrouping?.id ?? ""}
                />
              }
            />
            <InfoRow
              label={"Invoice curr."}
              value={customer.invoiceCurrency.code}
            />
            {showType && (
              <InfoRow
                label={"Type"}
                value={<ParentChildLink customer={customer} />}
              />
            )}
            <InfoRow
              label={"Name"}
              value={customer.name?.toUpperCase() ?? ""}
            />
            <InfoRow
              label={"Status"}
              value={customer.custStatus?.name?.toUpperCase() ?? ""}
            />
            <InfoRow
              label={"Rebate"}
              value={customer.hasRebateAgreements ? "Yes" : "No"}
            />
            <InfoRow label={"Seller"} value={customer.seller.name} />
            <InfoRow label={"Sales region"} value={customer.salesRegion.name} />
            <InfoRow
              label={"Created"}
              value={
                <TimestampWithName
                  isoString={customer.writeHistory.createdAt.isoString}
                  name={customer.writeHistory.createdBy.name}
                />
              }
            />
            <InfoRow
              label={"Modified"}
              value={
                <TimestampWithName
                  isoString={customer.writeHistory.modifiedAt?.isoString}
                  name={customer.writeHistory.modifiedBy?.name}
                />
              }
            />
          </ValueTable>
        </InfoTable>
      </CardBody>
    </Card>
  );
};
